import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Form, Input, Popconfirm, Row, Select, SelectProps, Space, Tooltip, Modal, DatePicker } from "antd";
import { useParams } from "react-router-dom";
import { Toast } from 'primereact/toast';
import HTTPService from "../../../utils/makerequest";
import { ICompanyData } from "../../../interfaces/ICompanyData";
import { IAuthentication, UserRole } from "../../../interfaces/authentication";
import { StateLoader } from "../../../redux/StateLoader";
import { IPageProps } from "../../../interfaces/page-data";
import MUIDatagrid, { IMUIDataGridColumns } from "../../../AltisComponent/MUIDataGrid";
import { IInventory } from "../../../interfaces/IItemData";
import { useTranslation } from "react-i18next";
import { DeleteOutlined, EditOutlined, EyeOutlined, UnorderedListOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import moment from "moment";

interface IAddActivityNameProps extends IPageProps {
    savedObj?: any;
    cancelModal?: () => void;
    companyId?: number;
    activityName?: string;
    maintenanceType?: number; // Add new prop
}

const AddActivityName: React.FunctionComponent<IAddActivityNameProps> = ({
    savedObj,
    cancelModal,
    companyId,
    activityName,
    maintenanceType // Add to destructuring
}) => {
    const [companyData, setCompanyData] = useState<ICompanyData[]>();
    const loader = new StateLoader();
    const userInfo: IAuthentication = loader.loadAuthState();
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const toast = useRef<any>(null);

    useEffect(() => {
        fetchData();

        if (companyId || activityName || maintenanceType) {
            form.setFieldsValue({
                companyId: companyId,
                activityName: activityName,
                maintenanceType: maintenanceType
            });
        }
    }, [companyId, activityName, maintenanceType]);


    const fetchData = async () => {
        const result = await HTTPService.GETCompanyNames();
        setCompanyData(result);
    };

    const filterOption: SelectProps<any>['filterOption'] = (input, option) => {
        return (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());
    };


    const registerActivity = (formData) => {
        const companyIdToUse = userInfo?.role === UserRole.AltisAdmin
            ? formData.companyId
            : userInfo?.companyId;

        const registerData = {
            companyId: companyIdToUse,
            activityName: formData.activityName,
            maintenanceType: formData.maintenanceType || maintenanceType
        };
        HTTPService.POST(`/Activity/registerActivity`, registerData)
            .then((res) => {
                if (res.status === 200) {
                    toast.current?.show({
                        severity: 'success',
                        summary: t('successful'),
                        detail: t('register_activity_msg'),
                        life: 3000
                    });
                    const currentUrl = window.location.href;
                    const redirectUrl = 'admin/add-activity-name';
                    if (currentUrl.includes(redirectUrl)) {
                        window.location.replace('/admin/activity-name');
                    } else {
                        savedObj(res.data)
                        cancelModal();
                    }
                } else {
                    toast.current?.show({
                        severity: 'error',
                        summary: t('mistake'),
                        detail: res.data || t('save_failed'),
                        life: 3000
                    });
                }
            })
            .catch((error) => {
                console.error('Save Error:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: t('mistake'),
                    detail: t('save_failed'),
                    life: 3000
                });
            });
    };

    const handleCancel = () => {
        if (cancelModal) {
            cancelModal();
        } else {
            window.location.replace("/admin/activity-name/");
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <Form
                form={form}
                layout="vertical"
                onFinish={registerActivity}
                initialValues={{
                    companyId: companyId,
                    activityName: activityName,
                    maintenanceType: maintenanceType
                }}
            >
                <Card style={{ padding: "50px" }}>
                    <h3>{t('register_activity')}</h3>
                    {userInfo?.role === UserRole.AltisAdmin && (
                        <Form.Item
                            name="companyId"
                            label={t('Select company')}
                            rules={[{ required: true, message: t('please_select_a_company') }]}
                        >
                            <Select showSearch placeholder={t('company_name')} style={{ marginBottom: '6px' }} filterOption={filterOption}>
                                {companyData?.map((company) => (
                                    <Select.Option key={company.companyId} value={company.companyId}>
                                        {company.companyName}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    {!maintenanceType && (
                        <Form.Item
                            name='maintenanceType'
                            label={t('Code Type')}
                            rules={[{ required: true, message: t('Code Type') }]}
                        >
                            <Select
                                showSearch
                                placeholder={t('Code Type')}
                                filterOption={filterOption}
                            >
                                <Select.Option value={1}>{t('maintenance_code')}</Select.Option>
                                <Select.Option value={2}>{t('fault_Code')}</Select.Option>
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item
                        label={t('activity_name')}
                        name="activityName"
                        rules={[{ required: true, message: t('enter_activity_name') }]}
                    >
                        <Input placeholder={t('enter_activity_name')} style={{ borderRadius: '5px' }} />
                    </Form.Item>

                    <Form.Item>
                        <Row gutter={[4, 4]} justify="end" align="middle">
                            <Col xs={12} sm={4}>
                                <Button
                                    block
                                    onClick={handleCancel}
                                    type="primary"
                                    style={{
                                        height: 64,
                                        backgroundColor: 'transparent',
                                        color: '#1890ff',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0 10px',
                                        border: 'none',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                                        e.currentTarget.style.color = '#40a9ff';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.style.boxShadow = 'none';
                                        e.currentTarget.style.color = '#1890ff';
                                    }}
                                >
                                    <CloseOutlined style={{ fontSize: 20 }} />
                                    <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                                        {t('cancel')}
                                    </span>
                                </Button>
                            </Col>
                            <Col xs={12} sm={4}>
                                <Button
                                    block
                                    htmlType="submit"
                                    type="primary"
                                    style={{
                                        height: 64,
                                        backgroundColor: 'transparent',
                                        color: '#1890ff',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0 10px',
                                        border: 'none',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                                        e.currentTarget.style.color = '#40a9ff';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.style.boxShadow = 'none';
                                        e.currentTarget.style.color = '#1890ff';
                                    }}
                                >
                                    <SaveOutlined style={{ fontSize: 20 }} />
                                    <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                                        {t('save')}
                                    </span>
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Card>
            </Form>
        </>
    );
};

export default AddActivityName;
